<template>
    <AppLayout title="Monitoring Pimpinan">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Monitoring Pengadaan
                <span v-if="isUnitScoped" class="text-base font-normal text-gray-600">(Unit)</span>
            </h2>
        </template>

        <div class="py-12">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
                    <div class="p-6">
                        <!-- Header Section -->
                        <div class="flex justify-between items-center mb-6">
                            <div>
                                <h3 class="text-lg font-medium text-gray-900">
                                    Monitoring Dokumen Pengadaan
                                </h3>
                                <p class="mt-1 text-sm text-gray-600">
                                    Pilih tahap dan status untuk melihat daftar dokumen pengadaan
                                    <span v-if="isUnitScoped">(hanya untuk unit Anda)</span>
                                </p>
                            </div>
                        </div>

                        <!-- Loading Spinner -->
                        <div v-if="initialLoading" class="mb-6 flex justify-center items-center py-12">
                            <div class="flex flex-col items-center">
                                <i class="pi pi-spin pi-spinner text-4xl text-blue-600 mb-4"></i>
                                <p class="text-gray-600 font-medium">Memuat data pengadaan...</p>
                            </div>
                        </div>

                        <!-- Pengadaan Selesai Card -->
                        <div v-if="!initialLoading" class="mb-6">
                            <div class="bg-green-50 border border-green-200 rounded-lg p-4 shadow-sm">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0">
                                            <i class="pi pi-check-circle text-green-600 text-2xl"></i>
                                        </div>
                                        <div class="ml-3">
                                            <h4 class="text-lg font-semibold text-green-800">Pengadaan Selesai</h4>
                                            <p class="text-sm text-green-700">Dokumen pengadaan yang sudah selesai</p>
                                        </div>
                                    </div>
                                    <div class="flex items-center">
                                        <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                            {{ completedCount }} dokumen
                                        </span>
                                        <button 
                                            @click="viewCompletedDocuments"
                                            class="ml-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
                                        >
                                            <i class="pi pi-eye mr-2"></i>
                                            Lihat Dokumen
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Tender Ditolak Card -->
                        <div v-if="!initialLoading" class="mb-6">
                            <div class="bg-red-50 border border-red-200 rounded-lg p-4 shadow-sm">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0">
                                            <i class="pi pi-times-circle text-red-500 text-2xl"></i>
                                        </div>
                                        <div class="ml-3">
                                            <h4 class="text-lg font-semibold text-red-800">Tender Ditolak</h4>
                                            <p class="text-sm text-red-600">Dokumen pengadaan yang ditolak</p>
                                        </div>
                                    </div>
                                    <div class="flex items-center">
                                        <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                            {{ rejectedCount }} dokumen
                                        </span>
                                        <button 
                                            @click="viewRejectedDocuments"
                                            class="ml-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                                        >
                                            <i class="pi pi-eye mr-2"></i>
                                            Lihat Dokumen
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- In Progress Card -->
                        <div v-if="!initialLoading" class="mb-6">
                            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 shadow-sm">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0">
                                            <i class="pi pi-spin pi-spinner text-blue-600 text-2xl"></i>
                                        </div>
                                        <div class="ml-3">
                                            <h4 class="text-lg font-semibold text-blue-800">Sedang Berjalan</h4>
                                            <p class="text-sm text-blue-700">Dokumen pengadaan yang sedang dalam proses</p>
                                        </div>
                                    </div>
                                    <div class="flex items-center">
                                        <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                            {{ inProgressCount }} dokumen
                                        </span>
                                        <button 
                                            @click="viewInProgressDocuments"
                                            class="ml-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                        >
                                            <i class="pi pi-eye mr-2"></i>
                                            Lihat Dokumen
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Tahap Cards Grid -->
                        <div v-if="!initialLoading" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                            <div v-for="tahap in tahapOptions" :key="tahap.kode_tahap" 
                                class="bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-sm cursor-pointer hover:bg-blue-50 transition"
                                :class="{'ring-2 ring-blue-400': tahap.kode_tahap === selectedTahap}" 
                                @click="selectTahap(tahap)">
                                <div class="font-semibold text-gray-800 mb-2 text-sm truncate">{{ tahap.nama_tahap }}</div>
                                <div class="flex flex-wrap gap-1">
                                    <span v-for="status in statusList" :key="status" 
                                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                        :class="badgeClass(status)">
                                        {{ statusLabel(status) }}
                                        <span class="ml-1">{{ tahap.count ? tahap.count[status] || 0 : 0 }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Status Filter Pills -->
                        <div v-if="selectedTahap" class="mb-4 flex flex-wrap gap-2">
                            <button v-for="status in statusList" :key="status"
                                class="px-3 py-1 rounded-full text-xs font-semibold focus:outline-none border"
                                :class="selectedStatus === status ? badgeClass(status) + ' text-white' : 'bg-white text-gray-700 border-gray-300'"
                                @click="selectStatus(status)">
                                {{ statusLabel(status) }}
                            </button>
                        </div>

                        <!-- Data Table -->
                        <DataTable
                            v-if="selectedTahap && selectedStatus"
                            :value="documents"
                            :paginator="true"
                            :rows="pageSize"
                            :totalRecords="totalRecords"
                            :lazy="true"
                            :loading="loading"
                            @page="onPage($event)"
                            @sort="onSort($event)"
                            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                            :rowsPerPageOptions="[5, 10, 25]"
                            currentPageReportTemplate="Menampilkan {first} sampai {last} dari {totalRecords} data"
                            responsiveLayout="scroll"
                            :globalFilterFields="['paket_pengadaan.nama_paket_pengadaan']"
                            class="p-datatable-sm"
                        >
                            <template #header>
                                <div class="flex justify-between items-center">
                                    <div class="p-input-icon-left">
                                        <i class="pi pi-search" />
                                        <InputText
                                            v-model="filters.global"
                                            placeholder="Cari nama paket..."
                                            @input="onSearch"
                                            class="p-inputtext-sm"
                                        />
                                    </div>
                                </div>
                            </template>

                            <template #empty>
                                <div class="text-center py-4">
                                    <i class="pi pi-info-circle text-gray-400 text-4xl mb-4"></i>
                                    <p class="text-gray-500">Tidak ada dokumen pengadaan pada tahap dan status yang dipilih</p>
                                </div>
                            </template>

                            <Column field="paket_pengadaan.nama_paket_pengadaan" header="Nama Paket Pengadaan" sortable>
                                <template #body="slotProps">
                                    <div class="font-medium">{{ slotProps.data.paket_pengadaan?.nama_paket_pengadaan || '-' }}</div>
                                </template>
                            </Column>

                            <Column field="paket_pengadaan.unit" header="Unit" sortable v-if="!isUnitScoped">
                                <template #body="slotProps">
                                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                        {{ slotProps.data.paket_pengadaan?.unit || '-' }}
                                    </span>
                                </template>
                            </Column>

                            <Column field="tahap.nama_tahap" header="Tahap" sortable>
                                <template #body="slotProps">
                                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {{ slotProps.data.tahap?.nama_tahap || '-' }}
                                    </span>
                                </template>
                            </Column>

                            <Column field="status" header="Status" sortable>
                                <template #body="slotProps">
                                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                        :class="statusBadgeClass(slotProps.data.status)">
                                        {{ (slotProps.data.status || '').toUpperCase() }}
                                    </span>
                                </template>
                            </Column>

                            <Column field="total_hps" header="Total HPS" sortable>
                                <template #body="slotProps">
                                    <div class="font-medium">{{ formatCurrency(slotProps.data.total_hps) }}</div>
                                </template>
                            </Column>

                            <Column field="created_at" header="Dibuat" sortable>
                                <template #body="slotProps">
                                    <div class="text-sm">{{ formatDate(slotProps.data.created_at) }}</div>
                                </template>
                            </Column>

                            <Column header="Aksi" :exportable="false" style="min-width: 100px">
                                <template #body="slotProps">
                                    <div class="flex gap-2">
                                        <Button
                                            icon="pi pi-list"
                                            class="p-button-rounded p-button-text p-button-success p-button-sm"
                                            @click="viewSummary(slotProps.data)"
                                            v-tooltip.top="'Lihat Summary'"
                                        />
                                    </div>
                                </template>
                            </Column>
                        </DataTable>

                        <!-- No Tahap Selected Message -->
                        <div v-if="!selectedTahap" class="text-center py-8">
                            <i class="pi pi-filter text-gray-400 text-4xl mb-4"></i>
                            <p class="text-gray-500">Silakan pilih tahap terlebih dahulu untuk melihat data</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
import { defineComponent } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import axios from 'axios'

export default defineComponent({
    name: 'MonitoringPimpinanDashboard',
    components: {
        AppLayout,
        DataTable,
        Column,
        Button,
        InputText
    },
    props: {
        isUnitScoped: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            documents: [],
            tahapOptions: [],
            selectedTahap: null,
            selectedStatus: 'proses',
            statusList: ['proses', 'revisi', 'direvisi', 'ditolak'],
            loading: false,
            initialLoading: true,
            totalRecords: 0,
            pageSize: 10,
            first: 0,
            sortField: null,
            sortOrder: null,
            filters: {
                global: ''
            },
            searchTimeout: null,
            completedCount: 0,
            rejectedCount: 0,
            inProgressCount: 0
        }
    },
    mounted() {
        // Load all initial data
        Promise.all([
            this.loadTahapOptions(),
            this.loadCompletedCount(),
            this.loadRejectedCount(),
            this.loadInProgressCount()
        ]).then(() => {
            this.initialLoading = false
        }).catch(() => {
            this.initialLoading = false
        })
    },
    methods: {
        async loadTahapOptions() {
            try {
                const response = await axios.get(route('monitoring_pimpinan.tahap_options'))
                
                if (response.data.success) {
                    this.tahapOptions = response.data.payload || []
                } else {
                    const errorMessage = response.data.error || response.data.message || 'Gagal memuat data tahap'
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: errorMessage,
                        life: 3000
                    })
                }
            } catch (error) {
                console.error('Error loading tahap options:', error)
                let errorMessage = 'Terjadi kesalahan saat memuat data tahap'
                
                if (error.response) {
                    const status = error.response.status
                    if (status === 403) {
                        errorMessage = 'Anda tidak memiliki akses untuk melihat data ini'
                    } else if (status === 401) {
                        errorMessage = 'Sesi Anda telah berakhir, silakan login kembali'
                    } else if (error.response.data?.error) {
                        errorMessage = error.response.data.error
                    }
                } else if (error.request) {
                    errorMessage = 'Koneksi bermasalah, silakan coba lagi'
                }
                
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: errorMessage,
                    life: 3000
                })
            }
        },

        selectTahap(tahap) {
            this.selectedTahap = tahap.kode_tahap
            this.selectedStatus = 'proses'
            this.first = 0
            this.loadDocuments()
        },

        selectStatus(status) {
            this.selectedStatus = status
            this.first = 0
            this.loadDocuments()
        },

        async loadDocuments() {
            if (!this.selectedTahap || !this.selectedStatus) return

            this.loading = true
            try {
                const response = await axios.get(
                    route('monitoring_pimpinan.documents') + 
                    this.buildQueryString()
                )
                
                if (response.data.success) {
                    this.documents = response.data.payload.data || []
                    this.totalRecords = response.data.payload.total || 0
                } else {
                    const errorMessage = response.data.error || response.data.message || 'Gagal memuat data dokumen pengadaan'
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: errorMessage,
                        life: 3000
                    })
                }
            } catch (error) {
                console.error('Error loading documents:', error)
                let errorMessage = 'Terjadi kesalahan saat memuat data'
                
                if (error.response) {
                    const status = error.response.status
                    if (status === 403) {
                        errorMessage = 'Anda tidak memiliki akses untuk melihat data ini'
                    } else if (status === 401) {
                        errorMessage = 'Sesi Anda telah berakhir, silakan login kembali'
                    } else if (error.response.data?.error) {
                        errorMessage = error.response.data.error
                    }
                } else if (error.request) {
                    errorMessage = 'Koneksi bermasalah, silakan coba lagi'
                }
                
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: errorMessage,
                    life: 3000
                })
            } finally {
                this.loading = false
            }
        },

        buildQueryString() {
            const params = new URLSearchParams()
            
            params.append('kode_tahap', this.selectedTahap)
            params.append('status', this.selectedStatus)
            params.append('page', Math.floor(this.first / this.pageSize) + 1)
            params.append('per_page', this.pageSize)
            
            if (this.sortField) {
                params.append('sort', this.sortField)
                params.append('direction', this.sortOrder === 1 ? 'asc' : 'desc')
            }
            
            if (this.filters.global) {
                params.append('search', this.filters.global)
            }
            
            return '?' + params.toString()
        },

        onPage(event) {
            this.first = event.first
            this.pageSize = event.rows
            this.loadDocuments()
        },

        onSort(event) {
            this.sortField = event.sortField
            this.sortOrder = event.sortOrder
            this.loadDocuments()
        },

        onSearch() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
            }
            
            this.searchTimeout = setTimeout(() => {
                this.first = 0 // Reset to first page
                this.loadDocuments()
            }, 500)
        },

        formatCurrency(value) {
            if (value === null || value === undefined) return 'Rp 0'
            return new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(value)
        },

        formatDate(dateString) {
            if (!dateString) return '-'
            return new Date(dateString).toLocaleDateString('id-ID', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        },

        badgeClass(status) {
            switch (status) {
                case 'proses': return 'bg-blue-500 text-white';
                case 'revisi': return 'bg-yellow-500 text-white';
                case 'direvisi': return 'bg-orange-500 text-white';
                case 'ditolak': return 'bg-red-500 text-white';
                default: return 'bg-gray-300 text-gray-800';
            }
        },

        statusBadgeClass(status) {
            switch (status) {
                case 'proses': return 'bg-blue-100 text-blue-800';
                case 'revisi': return 'bg-yellow-100 text-yellow-800';
                case 'direvisi': return 'bg-orange-100 text-orange-800';
                case 'ditolak': return 'bg-red-100 text-red-800';
                case 'selesai': return 'bg-green-100 text-green-800';
                case 'batal': return 'bg-gray-100 text-gray-800';
                default: return 'bg-gray-100 text-gray-800';
            }
        },

        statusLabel(status) {
            switch (status) {
                case 'proses': return 'Proses';
                case 'revisi': return 'Revisi';
                case 'direvisi': return 'Direvisi';
                case 'ditolak': return 'Ditolak';
                default: return status;
            }
        },

        viewSummary(document) {
            this.$inertia.visit(route('monitoring_pimpinan.summary', document.id))
        },

        async loadCompletedCount() {
            try {
                const response = await axios.get(route('monitoring_pimpinan.completed_count'))
                if (response.data.success) {
                    this.completedCount = response.data.payload || 0
                } else {
                    console.error('Failed to load completed count:', response.data.error)
                }
            } catch (error) {
                console.error('Error loading completed count:', error)
            }
        },

        async loadRejectedCount() {
            try {
                const response = await axios.get(route('monitoring_pimpinan.rejected_count'))
                if (response.data.success) {
                    this.rejectedCount = response.data.payload || 0
                } else {
                    console.error('Failed to load rejected count:', response.data.error)
                }
            } catch (error) {
                console.error('Error loading rejected count:', error)
            }
        },

        async loadInProgressCount() {
            try {
                const response = await axios.get(route('monitoring_pimpinan.in_progress_count'))
                if (response.data.success) {
                    this.inProgressCount = response.data.payload || 0
                } else {
                    console.error('Failed to load in-progress count:', response.data.error)
                }
            } catch (error) {
                console.error('Error loading in-progress count:', error)
            }
        },

        viewRejectedDocuments() {
            this.selectedTahap = 'all'
            this.selectedStatus = 'ditolak'
            this.first = 0
            this.loadDocuments()
        },

        viewCompletedDocuments() {
            this.selectedTahap = 'all'
            this.selectedStatus = 'selesai'
            this.first = 0
            this.loadDocuments()
        },

        viewInProgressDocuments() {
            this.selectedTahap = 'all'
            this.selectedStatus = 'proses'
            this.first = 0
            this.loadDocuments()
        }
    }
})
</script>
